import React, {useCallback, useMemo, useState} from 'react';
import {Alert, Button, Modal, Spin, Tooltip, Table, Tag} from 'antd';
import {
  CompressOutlined,
  ExpandOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import {useDispatch, useSelector} from 'react-redux';
import {getAllChapters} from '../../../Store/Actions/chapters';
import {getAllEventTypesByQuerySearch} from '../../../Store/Actions/eventTypes';
import {getUserDetails} from '../../../Store/Actions/users';
import Urls from '../../../Constant/Urls';
import {httpClient} from '../../../Api/client';
import CompareAuditData from '../../CommonComponents/CompareAuditData';
import RefundAuditLogView from './RefundAuditLogView';
import moment from 'moment';
import {transformAuditLogValue} from '../../../Util/auditLogValueTransform';

const ENTITY_TYPE_MAP = {
  Event: 'EVENT',
  'Event Option': 'EVENT_OPTION',
  'Event Registration': 'EVENT_REGISTRATION',
  'Event Refund': 'EVENT_REFUND',
  'Event Template Scheduler': 'EVENT_EMAIL_TEMPLATE',
  'Coupon Code': 'COUPON_CODE',
};

const AuditLogModalTrigger = ({
  userInfo,
  text,
  showIcon = true,
  buttonProps,
  localChapterData,
}) => {
  const dispatch = useDispatch();
  const {userDetails} = useSelector(({users}) => users);
  const allChapters = useSelector((state) => state.chapters?.allChapters);
  const allEventTypesSearch = useSelector(
    (state) => state.eventTypes?.allEventTypesQueryData?.forSearch,
  );
  const allEventsSearch = useSelector(
    (state) => state.events?.allEventsQueryData?.forSearch,
  );

  /** id -> name for resolving chapter IDs in compare view */
  const chapterIdToName = useMemo(() => {
    const list = allChapters?.data?.chapters;
    if (!Array.isArray(list)) return null;
    const map = new Map();
    list.forEach((ch) => {
      if (ch && ch.id != null) map.set(Number(ch.id), ch.name || String(ch.id));
    });
    return map;
  }, [allChapters]);

  /** id -> name for resolving event_type_id in compare view */
  const eventTypeIdToName = useMemo(() => {
    const list = allEventTypesSearch?.data?.eventTypes;
    if (!Array.isArray(list)) return null;
    const map = new Map();
    list.forEach((eventType) => {
      if (eventType && eventType.id != null) {
        map.set(Number(eventType.id), eventType.name || String(eventType.id));
      }
    });
    return map;
  }, [allEventTypesSearch]);

  /** id -> name for resolving event_id in coupon (and related) compare view */
  const eventIdToName = useMemo(() => {
    const map = new Map();
    if (userInfo?.event_id != null && userInfo?.event_name) {
      map.set(Number(userInfo.event_id), userInfo.event_name);
    }
    const list = allEventsSearch?.data?.events;
    if (Array.isArray(list)) {
      list.forEach((event) => {
        if (event && event.id != null) {
          map.set(Number(event.id), event.name || String(event.id));
        }
      });
    }
    return map.size > 0 ? map : null;
  }, [userInfo, allEventsSearch]);

  /** id -> display name for resolving create_by_id / edit_by_id in compare view */
  const userIdToDisplayName = useMemo(() => {
    const list = userDetails?.data;
    if (!Array.isArray(list)) return null;
    const map = new Map();
    list.forEach((user) => {
      if (user && user.id != null) {
        const name = `${user.first_name || ''} ${user.last_name || ''}`.trim();
        map.set(Number(user.id), name || user.email || `User ${user.id}`);
      }
    });
    return map;
  }, [userDetails]);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [histories, setHistories] = useState([]);
  const [selectedHistoryIds, setSelectedHistoryIds] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const entityType = ENTITY_TYPE_MAP[text] || null;
  const isRefundAuditLog = entityType === 'EVENT_REFUND';
  const resolvedRefundChapterData = useMemo(() => {
    if (localChapterData) return localChapterData;
    const chapterId = userInfo?.local_chapter_id;
    if (chapterId == null) return null;
    const chapters = allChapters?.data?.chapters;
    if (!Array.isArray(chapters)) return null;
    return (
      chapters.find((chapter) => Number(chapter.id) === Number(chapterId)) ||
      null
    );
  }, [localChapterData, userInfo, allChapters]);
  const isRegistrationAuditLog = entityType === 'EVENT_REGISTRATION';
  const isCouponCodeAuditLog = entityType === 'COUPON_CODE';
  const entityId = userInfo?.id;
  const entityName = useMemo(() => {
    if (isRefundAuditLog && userInfo) {
      const attendeeName = `${userInfo.attendee_first_name || ''} ${
        userInfo.attendee_last_name || ''
      }`.trim();
      if (attendeeName) return attendeeName;
    }
    if (isRegistrationAuditLog) {
      return userInfo?.name || text || 'Record';
    }
    if (isCouponCodeAuditLog) {
      return userInfo?.code || text || 'Record';
    }
    return (
      userInfo?.name || userInfo?.subject || userInfo?.code || text || 'Record'
    );
  }, [
    isRefundAuditLog,
    isRegistrationAuditLog,
    isCouponCodeAuditLog,
    userInfo,
    text,
  ]);
  const auditLogLabel = 'Audit Log';
  const modalTitle = `Audit Logs : ${entityName}`;

  const getHistoryTime = (history) =>
    history?.created_at ? new Date(history.created_at).getTime() : 0;

  const getHistorySortId = (history) =>
    typeof history?.id === 'number' ? history.id : 0;

  const compareHistoryAscending = (a, b) => {
    const timeDiff = getHistoryTime(a) - getHistoryTime(b);
    if (timeDiff !== 0) return timeDiff;
    return getHistorySortId(a) - getHistorySortId(b);
  };

  const isEmptyHistoryObject = (value) =>
    value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    Object.keys(value).length === 0;

  const isHistoryInsert = (history) => {
    if (history?._isInsertRow) return true;
    const oldValue = history?.old_value;
    const newValue = history?.new_value;
    const oldMissing = !oldValue || isEmptyHistoryObject(oldValue);
    const newMissing = !newValue || isEmptyHistoryObject(newValue);
    if (oldMissing && !newMissing) return true;
    return false;
  };

  const resolvedCreateById = useMemo(() => {
    if (userInfo?.create_by_id != null) {
      return userInfo.create_by_id;
    }
    for (let i = histories.length - 1; i >= 0; i -= 1) {
      const creatorId = histories[i]?.new_value?.create_by_id;
      if (creatorId != null) {
        return creatorId;
      }
    }
    return null;
  }, [userInfo, histories]);

  const registrationInsertRow = useMemo(() => {
    if (!isRegistrationAuditLog || !entityId) {
      return null;
    }

    const oldestHistory = histories[histories.length - 1];
    const oldBaseline = oldestHistory?.old_value;
    const insertSnapshot =
      oldBaseline && !isEmptyHistoryObject(oldBaseline)
        ? oldBaseline
        : oldestHistory?.new_value || userInfo || {};

    return {
      id: `insert-${entityId}`,
      operationType: 'Insert',
      modified_by_id: userInfo?.create_by_id || null,
      created_at: userInfo?.created_at || null,
      _isInsertRow: true,
      _snapshot: insertSnapshot,
    };
  }, [isRegistrationAuditLog, entityId, histories, userInfo]);

  const standardInsertRow = useMemo(() => {
    if (isRefundAuditLog || isRegistrationAuditLog) {
      return null;
    }
    if (!entityId || histories.some(isHistoryInsert)) {
      return null;
    }

    const oldestHistory = histories[histories.length - 1];
    // Baseline = state before the first recorded change (old_value), not after (new_value).
    const oldBaseline = oldestHistory?.old_value;
    const insertSnapshot =
      oldBaseline && !isEmptyHistoryObject(oldBaseline)
        ? oldBaseline
        : oldestHistory?.new_value || userInfo || {};

    return {
      id: `insert-${entityId}`,
      operationType: 'Insert',
      modified_by_id: resolvedCreateById,
      created_at: userInfo?.created_at || null,
      _isInsertRow: true,
      _snapshot: insertSnapshot,
    };
  }, [
    isRefundAuditLog,
    isRegistrationAuditLog,
    entityId,
    histories,
    userInfo,
    resolvedCreateById,
  ]);

  const insertRow = isRegistrationAuditLog
    ? registrationInsertRow
    : standardInsertRow;

  const tableRows = useMemo(() => {
    const rows = insertRow ? [...histories, insertRow] : [...histories];
    return rows.sort((a, b) => compareHistoryAscending(b, a));
  }, [histories, insertRow]);

  const registrationCreatorUser =
    userDetails?.data && userInfo?.create_by_id
      ? userDetails.data.find((item) => item.id === userInfo.create_by_id)
      : null;
  const creatorUser = isRegistrationAuditLog
    ? registrationCreatorUser
    : userDetails?.data && resolvedCreateById != null
    ? userDetails.data.find((item) => item.id === resolvedCreateById)
    : null;
  const selectedHistories = useMemo(
    () =>
      selectedHistoryIds
        .map((id) => tableRows.find((item) => item.id === id))
        .filter(Boolean),
    [tableRows, selectedHistoryIds],
  );

  const modifierUser =
    selectedHistories?.[selectedHistories.length - 1] && userDetails?.data
      ? userDetails.data.find(
          (item) =>
            item.id ===
            selectedHistories[selectedHistories.length - 1].modified_by_id,
        )
      : null;

  const transformHistoryValue = useCallback(
    (value) =>
      transformAuditLogValue(value, entityType, {
        chapterIdToName,
        eventTypeIdToName,
        eventIdToName,
        userIdToDisplayName,
      }),
    [entityType, chapterIdToName, eventTypeIdToName, eventIdToName, userIdToDisplayName],
  );

  const getOperationType = (history) => {
    if (history?._isInsertRow) return 'Insert';
    const oldValue = history?.old_value;
    const newValue = history?.new_value;

    const oldMissing = !oldValue || isEmptyHistoryObject(oldValue);
    const newMissing = !newValue || isEmptyHistoryObject(newValue);

    if (oldMissing && !newMissing) return 'Insert';
    if (!oldMissing && newMissing) return 'Delete';
    return 'Update';
  };

  const formatUserEmail = (history) => {
    if (!history) return '-';
    if (history.modified_by_role === 'System') {
      return 'System';
    }
    const modifiedById = history.modified_by_id;
    if (modifiedById == null) {
      return '-';
    }
    const modifiedUser = userDetails?.data?.find(
      (item) => item.id === modifiedById,
    );
    return modifiedUser?.email || `User ${modifiedById}`;
  };

  const formatModifierDetail = (history) => {
    if (!history) return '-';
    if (history.modified_by_role === 'System') {
      return 'System';
    }
    const modifiedById = history.modified_by_id;
    if (modifiedById == null) {
      return '-';
    }
    const modifiedUser = userDetails?.data?.find(
      (item) => item.id === modifiedById,
    );
    if (modifiedUser) {
      const name = `${(modifiedUser.first_name || '').trim()} ${(
        modifiedUser.last_name || ''
      ).trim()}`.trim();
      return `${name || modifiedUser.email} (ID: ${modifiedById})`;
    }
    return `User ${modifiedById} (ID: ${modifiedById})`;
  };

  const fetchHistory = useCallback(
    async (page = 1, limit = pageCount) => {
      if (!entityType || !entityId) {
        setError('Audit history is not available for this record.');
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await httpClient.get(Urls.HISTORY_GET_LATEST, {
          params: {
            entity: entityType,
            entityId,
            page,
            pageCount: limit,
          },
        });

        if (response?.status === 200) {
          const data = response?.data?.data || {};
          const nextHistories = data.histories || [];
          setHistories(nextHistories);
          setCurrentPage(data.currentPage || page);
          setPageCount(data.pageCount || limit);
          setTotalCount(data.totalCount || 0);
          setSelectedHistoryIds([]);
          setCompareMode(false);
        } else {
          setError('Failed to fetch audit history.');
        }
      } catch (err) {
        setError('Failed to fetch audit history.');
      } finally {
        setLoading(false);
      }
    },
    [entityType, entityId, pageCount],
  );

  const handleOpen = () => {
    setVisible(true);
    // Load lookup data so compare diff shows names instead of raw IDs/enums.
    if (!userDetails?.data) {
      dispatch(getUserDetails());
    }
    if (
      entityType === 'EVENT' ||
      entityType === 'EVENT_EMAIL_TEMPLATE' ||
      entityType === 'EVENT_REFUND'
    ) {
      if (!allChapters) {
        dispatch(getAllChapters());
      }
    }
    if (entityType === 'EVENT') {
      if (!allEventTypesSearch?.data?.eventTypes) {
        dispatch(
          getAllEventTypesByQuerySearch('', '', '', {
            page: 1,
            pageCount: 500,
          }),
        );
      }
    }
    fetchHistory(1, pageCount);
  };

  const handleClose = () => {
    setVisible(false);
    setIsFullScreen(false);
    setSelectedHistoryIds([]);
    setCompareMode(false);
  };

  const handleCompareClick = () => {
    if (selectedHistoryIds.length === 2) {
      setCompareMode(true);
    }
  };

  const getHistorySnapshot = useCallback(
    (history) => {
      if (!history) return transformHistoryValue(userInfo);
      if (history?._isInsertRow) {
        return transformHistoryValue(history._snapshot ?? userInfo);
      }
      // Each history row's new_value is the entity state after that change.
      return transformHistoryValue(
        history.new_value ?? history.old_value ?? userInfo,
      );
    },
    [transformHistoryValue, userInfo],
  );

  const sortedSelectedHistories = useMemo(
    () => [...selectedHistories].sort(compareHistoryAscending),
    [selectedHistories],
  );

  const olderHistory = sortedSelectedHistories[0] || null;
  const newerHistory = sortedSelectedHistories[1] || null;

  const compareValues = useMemo(() => {
    if (!olderHistory || !newerHistory) {
      return {oldValue: null, newValue: null};
    }

    return {
      oldValue: getHistorySnapshot(olderHistory),
      newValue: getHistorySnapshot(newerHistory),
    };
  }, [olderHistory, newerHistory, getHistorySnapshot]);

  const historyColumns = [
    {
      title: 'Operation Type',
      dataIndex: 'operationType',
      key: 'operationType',
      width: '35%',
      render: (_, row) => {
        const operationType = getOperationType(row);
        const color =
          operationType === 'Insert'
            ? 'green'
            : operationType === 'Delete'
            ? 'red'
            : 'blue';
        return <Tag color={color}>{operationType}</Tag>;
      },
    },
    {
      title: 'User Email',
      dataIndex: 'modified_by_id',
      key: 'userEmail',
      width: '35%',
      render: (modifiedById, row) => formatUserEmail(row),
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'createdAt',
      width: '30%',
      render: (date) => (date ? moment(date).format('MMM DD, YYYY') : '-'),
    },
  ];

  const toggleFullScreen = () => {
    setIsFullScreen((prev) => !prev);
  };

  return (
    <>
      {showIcon ? (
        <div className='action-container' onClick={handleOpen}>
          <div className='icon-container' id={`AuditLog-${userInfo?.id}`}>
            <FileTextOutlined /> {auditLogLabel}
          </div>
        </div>
      ) : (
        // Plain div: avoids Ant Button/link underline and .saveAndCancelLink:hover underline
        <div
          role='button'
          tabIndex={0}
          className={buttonProps?.className}
          id={`AuditLog-${userInfo?.id}`}
          onClick={handleOpen}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleOpen(e);
            }
          }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            color: buttonProps?.style?.color || 'inherit',
            cursor: 'pointer',
            userSelect: 'none',
            textDecoration: 'none',
            padding: buttonProps?.style?.padding ?? 0,
            height: buttonProps?.style?.height ?? 'auto',
            border: 'none',
            background: 'transparent',
            boxShadow: 'none',
            outline: 'none',
          }}>
          <FileTextOutlined style={{marginRight: 6, fontSize: 14}} />
          {auditLogLabel}
        </div>
      )}

      <Modal
        title={
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              paddingRight: 8,
            }}>
            <span>{modalTitle}</span>
            <Tooltip title={isFullScreen ? 'Exit full screen' : 'Full screen'}>
              <Button
                type='default'
                className='audit-log-fullscreen-btn'
                icon={isFullScreen ? <CompressOutlined /> : <ExpandOutlined />}
                onClick={toggleFullScreen}
                aria-label={isFullScreen ? 'Exit full screen' : 'Full screen'}
              />
            </Tooltip>
          </div>
        }
        open={visible}
        onCancel={handleClose}
        closeIcon={null}
        footer={
          <div style={{textAlign: 'right'}}>
            <Button onClick={handleClose}>Close</Button>
          </div>
        }
        width={isFullScreen ? '96vw' : 900}
        styles={{
          body: {
            height: isFullScreen ? 'calc(100vh - 180px)' : undefined,
            overflow: 'hidden',
          },
          content: isFullScreen
            ? {
                margin: 0,
                paddingBottom: 0,
                display: 'flex',
                flexDirection: 'column',
                maxHeight: 'calc(100vh - 80px)',
              }
            : undefined,
        }}
        style={
          isFullScreen
            ? {
                top: 32,
                paddingBottom: 0,
                maxWidth: '96vw',
              }
            : undefined
        }
        destroyOnClose>
        {loading && (
          <div style={{textAlign: 'center', padding: '24px 0'}}>
            <Spin />
          </div>
        )}
        {!loading && error && (
          <Alert
            type='error'
            message={error}
            showIcon
            style={{marginBottom: 16}}
          />
        )}
        {!loading && !error && isRefundAuditLog && (
          <RefundAuditLogView
            histories={histories}
            totalCount={totalCount}
            currentPage={currentPage}
            pageCount={pageCount}
            onPageChange={(page) => fetchHistory(page, pageCount)}
            userDetails={userDetails}
            localChapterData={resolvedRefundChapterData}
            transformOptions={{
              chapterIdToName,
              eventTypeIdToName,
              eventIdToName,
              userIdToDisplayName,
            }}
          />
        )}
        {!loading && !error && !isRefundAuditLog && (
          <>
            <div
              style={{
                marginBottom: 12,
                fontSize: 12,
                color: '#666',
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '4px 24px',
                maxWidth: '100%',
              }}>
              <div>
                <strong>Created By:</strong>{' '}
                {isRegistrationAuditLog ? (
                  <>
                    {registrationCreatorUser
                      ? `${(registrationCreatorUser.first_name || '').trim()} ${(
                          registrationCreatorUser.last_name || ''
                        ).trim()}`.trim() || `User ${userInfo.create_by_id}`
                      : userInfo?.create_by_id
                      ? `User ${userInfo.create_by_id}`
                      : '-'}{' '}
                    {userInfo?.create_by_id && `(ID: ${userInfo.create_by_id})`}
                  </>
                ) : (
                  <>
                    {creatorUser
                      ? `${(creatorUser.first_name || '').trim()} ${(
                          creatorUser.last_name || ''
                        ).trim()}`.trim() || creatorUser.email || `User ${resolvedCreateById}`
                      : resolvedCreateById != null
                      ? `User ${resolvedCreateById}`
                      : '-'}{' '}
                    {resolvedCreateById != null && `(ID: ${resolvedCreateById})`}
                  </>
                )}
              </div>
              {selectedHistories.length > 0 && (
                <div>
                  <strong>Modified By:</strong>{' '}
                  {isRegistrationAuditLog ? (
                    <>
                      {modifierUser
                        ? `${(modifierUser.first_name || '').trim()} ${(
                            modifierUser.last_name || ''
                          ).trim()}`.trim() ||
                          `User ${
                            selectedHistories[selectedHistories.length - 1]
                              .modified_by_id
                          }`
                        : `User ${
                            selectedHistories[selectedHistories.length - 1]
                              .modified_by_id
                          }`}{' '}
                      (ID:{' '}
                      {
                        selectedHistories[selectedHistories.length - 1]
                          .modified_by_id
                      }
                      )
                    </>
                  ) : (
                    formatModifierDetail(
                      selectedHistories[selectedHistories.length - 1],
                    )
                  )}
                </div>
              )}
              <div>
                <strong>Created At:</strong>{' '}
                {userInfo?.created_at
                  ? moment(userInfo.created_at).format('DD MMM YYYY, hh:mm A')
                  : '-'}
              </div>
              {selectedHistories.length > 0 && (
                <div>
                  <strong>Modified At:</strong>{' '}
                  {selectedHistories[selectedHistories.length - 1].created_at
                    ? moment(
                        selectedHistories[selectedHistories.length - 1]
                          .created_at,
                      ).format('DD MMM YYYY, hh:mm A')
                    : '-'}
                </div>
              )}
            </div>
            {!compareMode && (
              <div style={{marginBottom: 12}}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}>
                  <div
                    style={{fontSize: 12, color: '#666', marginRight: 'auto'}}>
                    Total Records : {totalCount}
                  </div>
                  <Button
                    type='primary'
                    size='small'
                    onClick={handleCompareClick}
                    disabled={selectedHistoryIds.length !== 2}>
                    Compare
                  </Button>
                </div>
                <Table
                  size='small'
                  rowKey='id'
                  columns={historyColumns}
                  dataSource={tableRows}
                  pagination={{
                    current: currentPage,
                    pageSize: pageCount,
                    total: totalCount,
                    showSizeChanger: false,
                    onChange: (page) => fetchHistory(page, pageCount),
                  }}
                  rowSelection={{
                    type: 'checkbox',
                    selectedRowKeys: selectedHistoryIds,
                    onChange: (selectedRowKeys) => {
                      const lastTwo = selectedRowKeys.slice(-2);
                      setSelectedHistoryIds(lastTwo);
                    },
                    getCheckboxProps: (record) => ({
                      disabled:
                        selectedHistoryIds.length >= 2 &&
                        !selectedHistoryIds.includes(record.id),
                    }),
                  }}
                />
                <div style={{fontSize: 12, color: '#666', marginTop: 6}}>
                  Select exactly 2 records to compare.
                </div>
              </div>
            )}
            {compareMode && olderHistory && newerHistory && (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 8,
                  }}>
                  <div style={{fontSize: 12, color: '#666'}}>
                    Comparing:{' '}
                    {moment(olderHistory.created_at).format(
                      'MMM DD, YYYY hh:mm A',
                    )}{' '}
                    {'->'}{' '}
                    {moment(newerHistory.created_at).format(
                      'MMM DD, YYYY hh:mm A',
                    )}
                  </div>
                  <Button size='small' onClick={() => setCompareMode(false)}>
                    Back to Audit Logs
                  </Button>
                </div>
                <div
                  style={{
                    height: isFullScreen ? 'calc(100vh - 360px)' : '52vh',
                  }}>
                  <CompareAuditData
                    oldValue={compareValues.oldValue}
                    newValue={compareValues.newValue}
                  />
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default AuditLogModalTrigger;
